import { GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import { ChangeEvent, useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { useContactCaptureService } from '../services';
import { ContactCaptureStatus, ContactCaptureType } from '../types';

export const useContactCaptureListController = () => {
  const companyId = useSelector(getCurrentCompanyId) as string | undefined;
  const searchTimer = useRef<number>();
  const service = useContactCaptureService();

  const reload = useCallback(async (override?: Partial<typeof service.filter>) => {
    if (!companyId) return;
    await service.getList(companyId, override);
  }, [companyId, service.getList]);

  const changeSearch = useCallback((_event: ChangeEvent<HTMLInputElement>, query: string) => {
    const next = { query, page: 0, offset: 0 };
    service.changeFilter(next);
    window.clearTimeout(searchTimer.current);
    searchTimer.current = window.setTimeout(() => void reload(next).catch(() => undefined), 400);
  }, [reload, service.changeFilter]);

  const changeType = useCallback((type: ContactCaptureType | '') => {
    const next = { type: type || undefined, page: 0, offset: 0 };
    service.changeFilter(next);
    void reload(next).catch(() => undefined);
  }, [reload, service.changeFilter]);

  const changeStatus = useCallback((status: ContactCaptureStatus | '') => {
    const next = { status: status || undefined, page: 0, offset: 0 };
    service.changeFilter(next);
    void reload(next).catch(() => undefined);
  }, [reload, service.changeFilter]);

  const changeSorting = useCallback((model: GridSortModel) => {
    if (!model.length || !model[0].sort) return;
    const next = { sort: `${model[0].field}_${model[0].sort}`, page: 0, offset: 0 };
    service.changeFilter(next);
    void reload(next).catch(() => undefined);
  }, [reload, service.changeFilter]);

  const changePagination = useCallback((model: GridPaginationModel) => {
    const next = { page: model.page, limit: model.pageSize, offset: model.page * model.pageSize };
    service.changeFilter(next);
    void reload(next).catch(() => undefined);
  }, [reload, service.changeFilter]);

  useEffect(() => {
    window.clearTimeout(searchTimer.current);
    service.clearList();
    service.changeFilter({ page: 0, offset: 0 });
    if (companyId) void service.getList(companyId, { page: 0, offset: 0 }).catch(() => undefined);

    return () => window.clearTimeout(searchTimer.current);
  }, [companyId]);

  return {
    companyId,
    loading: service.loading,
    filter: service.filter,
    list: service.list,
    listCompanyId: service.listCompanyId,
    error: service.error,
    reload,
    changeSearch,
    changeType,
    changeStatus,
    changeSorting,
    changePagination,
    updateStatus: service.mutateStatus,
    duplicate: service.duplicate,
    remove: service.remove,
  };
};
